import { useState, useMemo } from "react";
import { useQuery } from "@apollo/client";
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Chip,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import ReusableTable from "../../../../../components/ReusableTable/ReusableTable";
import TableSkeleton from "../../../../../components/Skeletons/TableSkeleton";
import {
  GET_ASSESSMENT,
  GET_USERS,
  GET_AUDIT_ASSESSMENT,
  GET_AUDIT_USERS,
} from "../../../../../apis/settingsApis/access-apis/accessApis";
import { formatDate, getStartOfDayUTC, getEndOfDayUTC, formatDateForInput } from "../../../../../utils/dateUtils";
import { AssessmentStatus } from "../../../../../utils/enums";
import { AccessReviewStatusChip } from "../../../../../components/AccessReviewStatusChip/AccessReviewStatusChip";

const AccessAssessmentsTab = ({ audit, isAuditorView = false }) => {
  const theme = useTheme();
  const [selectedAssessmentIdx, setSelectedAssessmentIdx] = useState(0);

  // Calculate 12-month cutoff date from audit_end_date for backend filtering
  const { start_date, end_date } = useMemo(() => {
    if (!audit?.audit_end_date) return { start_date: null, end_date: null };

    const endDate = new Date(audit.audit_end_date);

    const cutoffDate = new Date(endDate);
    cutoffDate.setMonth(cutoffDate.getMonth() - 12);

    // Convert to ISO string for backend using utility functions
    return {
      start_date: getStartOfDayUTC(formatDateForInput(cutoffDate)),
      end_date: audit.audit_end_date,
    };
  }, [audit?.audit_end_date]);

  // Fetch assessments with backend filtering
  const { data: assessmentsData, loading: assessmentsLoading } = useQuery(
    isAuditorView ? GET_AUDIT_ASSESSMENT : GET_ASSESSMENT,
    {
      variables: {
        ...(isAuditorView && { audit_id: audit?.id }),
        page: 1,
        limit: 1000,
        filter: {
          start_date,
          end_date,
        },
      },
      skip: !start_date || !end_date || (isAuditorView && !audit?.id),
      fetchPolicy: "cache-and-network",
    }
  );

  // Get filtered assessments directly from backend - only show completed
  const filteredAssessments = useMemo(() => {
    const allAssessments = isAuditorView
      ? assessmentsData?.getAuditAssessment?.data || []
      : assessmentsData?.getAssessment?.data || [];
    return allAssessments.filter(
      (assessment) => assessment.status_title === "completed"
    );
  }, [assessmentsData, isAuditorView]);

  const currentAssessment = filteredAssessments[selectedAssessmentIdx];

  // Fetch users for selected assessment
  const {
    data: usersData,
    loading: usersLoading,
  } = useQuery(isAuditorView ? GET_AUDIT_USERS : GET_USERS, {
    variables: {
      ...(isAuditorView && { audit_id: audit?.id }),
      page: 1,
      limit: 1000,
      filter: {
        resource_id: currentAssessment?.resource_id
          ? [currentAssessment.resource_id]
          : [],
        ...(currentAssessment?.id && { assessment_id: currentAssessment.id }),
      },
    },
    skip: !currentAssessment?.resource_id || (isAuditorView && !audit?.id),
    fetchPolicy: "network-only",
  });

  const users = useMemo(() => {
    return isAuditorView
      ? usersData?.getAuditUsers?.data || []
      : usersData?.getUsers?.data || [];
  }, [usersData, isAuditorView]);

  const getIconComponent = (name) => {
    if (name === "Schedule") return <ScheduleIcon sx={{ fontSize: 16 }} />;
    if (name === "Check") return <CheckCircleIcon sx={{ fontSize: 16 }} />;
    return null;
  };

  const getStatusChip = (statusTitle) => {
    const status = (statusTitle || "").toLowerCase();

    if (status === AssessmentStatus.DRAFT) {
      return {
        label: "Draft",
        color: theme.customColors?.status?.warning?.text || "#D97706",
        bg: theme.customColors?.status?.warning?.bg || "#FEF3C7",
        icon: "Schedule",
      };
    } else if (status === AssessmentStatus.COMPLETED) {
      return {
        label: "Completed",
        color: theme.customColors?.status?.success?.text || "#059669",
        bg: theme.customColors?.status?.success?.bg || "#D1FAE5",
        icon: "Check",
      };
    } else if (status === AssessmentStatus.REJECTED) {
      return {
        label: "Rejected",
        color: theme.customColors?.status?.error?.text || "#DC2626",
        bg: theme.customColors?.status?.error?.bg || "#FEE2E2",
        icon: null,
      };
    }

    return {
      label: statusTitle || "Unknown",
      color: theme.palette.text.secondary,
      bg: theme.palette.grey[200],
      icon: null,
    };
  };

  const renderStatusMessage = () => {
    const status = (currentAssessment?.status_title || "").toLowerCase();

    if (status === AssessmentStatus.COMPLETED) {
      return (
        <Box>
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ color: theme.customColors?.text?.strong || "#111827" }}
          >
            Access Review approved at{" "}
            {formatDate(currentAssessment?.completed_at)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.customColors?.text?.secondary || "#6B7280",
              mt: 0.5,
            }}
          >
            Started at {formatDate(currentAssessment?.created_at)}
          </Typography>
        </Box>
      );
    }

    if (status === AssessmentStatus.REJECTED) {
      return (
        <Box>
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ color: theme.customColors?.text?.strong || "#111827" }}
          >
            Access Review discarded at{" "}
            {formatDate(currentAssessment?.updated_at)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.customColors?.text?.secondary || "#6B7280",
              mt: 0.5,
            }}
          >
            Started at {formatDate(currentAssessment?.created_at)}
          </Typography>
        </Box>
      );
    }

    return (
      <Typography
        variant="h5"
        fontWeight={600}
        sx={{ color: theme.customColors?.text?.strong || "#111827" }}
      >
        Access Review started at {formatDate(currentAssessment?.created_at)}
      </Typography>
    );
  };

  // Table columns for users
  const columns = [
    { label: "Name", field: "user_name", width: "20%" },
    { label: "Email", field: "user_email", width: "20%" },
    { label: "Role", field: "role", width: "20%" },
    {
      label: "Decision",
      field: "review_status",
      width: "15%",
      align: "center",
    },
    {
      label: "Comments",
      field: "review_notes",
      width: "25%",
      align: "center",
    },
  ];

  // Render custom cells
  const renderCell = (row, column) => {
    switch (column.field) {
      case "user_name":
        return (
          <Box display="flex" alignItems="center">
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: theme.customColors?.text?.strong || "#374151",
                fontFamily: theme.typography.fontFamily,
                lineHeight: 1.35,
                letterSpacing: "0.01em",
              }}
            >
              {row.name || "Unknown User"}
            </Typography>
          </Box>
        );

      case "user_email":
        return (
          <Typography
            variant="body2"
            sx={{ color: theme.customColors?.text?.secondary || "#6B7280" }}
          >
            {row.email || "-"}
          </Typography>
        );

      case "role":
        const roleText = row.role || "No Role";
        return (
          <Tooltip title={roleText} arrow placement="top">
            <Chip
              label={roleText}
              size="medium"
              sx={{
                borderRadius: "0px",
                backgroundColor: "#1976d214",
                color: "#1976d2",
                fontWeight: 500,
                fontSize: "0.75rem",
                maxWidth: "100%",
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            />
          </Tooltip>
        );

      case "review_status":
        return (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <AccessReviewStatusChip status={row.review_status || "Approved"} />
          </Box>
        );

      case "review_notes":
        return (
          <Typography
            variant="body2"
            sx={{
              color: theme.customColors?.text?.secondary || "#6B7280",
              textAlign: "center",
              fontStyle: row.review_notes ? "normal" : "italic",
            }}
          >
            {row.review_notes || "-"}
          </Typography>
        );

      default:
        return (
          <Typography
            variant="body2"
            sx={{ color: theme.customColors?.text?.strong || "#111827" }}
          >
            {row[column.field]}
          </Typography>
        );
    }
  };

  if (assessmentsLoading) {
    return (
      <Card
        sx={{
          minHeight: "600px",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #E8EAED",
          overflow: "hidden",
        }}
        variant="outlined"
      >
        <CardHeader
          title={
            <Typography
              variant="h5"
              sx={{ fontSize: "18px", fontWeight: "bold" }}
            >
              Access Assessments
            </Typography>
          }
          sx={{
            borderBottom: "1px solid #ddd",
            background: "#F7F7FC",
            padding: "14px",
          }}
        />
        <CardContent sx={{ p: 0 }}>
          <TableSkeleton />
        </CardContent>
      </Card>
    );
  }

  if (filteredAssessments.length === 0) {
    return (
      <Card
        sx={{
          minHeight: "600px",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #E8EAED",
          overflow: "hidden",
        }}
        variant="outlined"
      >
        <CardHeader
          title={
            <Typography
              variant="h5"
              sx={{ fontSize: "18px", fontWeight: "bold" }}
            >
              Access Assessments
            </Typography>
          }
          sx={{
            borderBottom: "1px solid #ddd",
            background: "#F7F7FC",
            padding: "14px",
          }}
        />
        <CardContent>
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography
              variant="body2"
              sx={{ color: theme.customColors?.text?.secondary || "#6B7280" }}
            >
              No access assessments found within the audit period.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const statusChip = getStatusChip(currentAssessment?.status_title);

  return (
    <Card
      sx={{
        minHeight: "600px",
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        border: "1px solid #E8EAED",
        overflow: "hidden",
      }}
      variant="outlined"
    >
      <CardHeader
        title={
          <Typography
            variant="h5"
            sx={{ fontSize: "18px", fontWeight: "bold" }}
          >
            Access Assessments
          </Typography>
        }
        sx={{
          borderBottom: "1px solid #ddd",
          background: "#F7F7FC",
          padding: "14px",
        }}
      />
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={2.25}>
          {/* Assessment Dropdown */}
          <FormControl sx={{ minWidth: 320, mb: 2 }} size="small">
            <InputLabel id="access-assessment-select-label">
              Select Assessment
            </InputLabel>
            <Select
              labelId="access-assessment-select-label"
              value={selectedAssessmentIdx}
              label="Select Assessment"
              onChange={(e) => setSelectedAssessmentIdx(e.target.value)}
            >
              {filteredAssessments.map((assessment, idx) => (
                <MenuItem key={assessment?.id || idx} value={idx}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.customColors?.text?.strong || "#111827",
                      }}
                    >
                      {assessment?.system_name || "Unknown System"} -{" "}
                      {assessment?.title} - {formatDate(assessment?.created_at)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          theme.customColors?.text?.secondary || "#6B7280",
                      }}
                    >
                      {assessment?.status_title}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Assessment Status Card */}
          {currentAssessment && (
            <Card
              variant="outlined"
              sx={{
                mb: 2,
                backgroundColor:
                  theme.customColors?.background?.cardHeader || "#F9FAFB",
                border: `1px solid ${theme.customColors?.border?.subtle || "#E5E7EB"}`,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    {renderStatusMessage()}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color:
                            theme.customColors?.text?.secondary || "#6B7280",
                        }}
                      >
                        System:{" "}
                        {currentAssessment?.system_name || "Unknown"} |
                        Created by:{" "}
                        {currentAssessment?.reviewer_email || "-"}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={statusChip.label}
                    icon={getIconComponent(statusChip.icon)}
                    size="small"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      borderRadius: 1,
                      border: "none",
                      minWidth: 90,
                      textAlign: "center",
                      backgroundColor: statusChip.bg,
                      color: statusChip.color,
                      "& .MuiChip-icon": {
                        color: "inherit",
                        fontSize: 16,
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Users Table */}
          {usersLoading ? (
            <TableSkeleton />
          ) : (
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: theme.customColors?.text?.strong || "#111827",
                  mb: 2,
                }}
              >
                Users ({users.length})
              </Typography>
              <ReusableTable
                columns={columns}
                data={users}
                renderCell={renderCell}
                rowsPerPageOptions={[10, 20, 50, 100]}
                height="auto"
                showPagination={true}
                sx={{
                  "& .MuiTableHead-root": {
                    bgcolor: "background.paper",
                  },
                  "& .MuiTableCell-head": {
                    fontWeight: 600,
                    color: "text.primary",
                    borderBottom: `2px solid ${theme.customColors?.border?.main || "#D1D5DB"}`,
                  },
                  "& .MuiTableRow-root": {
                    "&:hover": {
                      bgcolor: "rgba(3, 27, 137, 0.04)",
                    },
                  },
                  "& .MuiTableCell-root": {
                    borderBottom: `1px solid ${theme.customColors?.border?.light || "#E5E7EB"}`,
                  },
                }}
              />
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AccessAssessmentsTab;
